export type RoomAssetLink = {
  sfid?: string;
  name: string;
};

export const assetKey = (asset: RoomAssetLink) => asset.sfid ?? asset.name;

// In-app only: never send to Salesforce. Without it, same-named local assets collapse into one.
export const LOCAL_ASSET_ID_PREFIX = 'local:';

export const newLocalAssetId = () => `${LOCAL_ASSET_ID_PREFIX}${crypto.randomUUID()}`;

export type UpliftType = 'property_net' | 'bundled_experience' | 'combined';

// A linked experience available to bundle into a package upgrade.
export type Experience = {
  id: string;
  luxPlus: string;
  travelTo: string;
  accountName: string;
  dealOptionName: string;
  fareType: string;
  dealCategory: string;
  subCategory: string;
  ancillarySellPriceUplift: number;
  valueCostCurrency: number;
  currency: string;
  destination: string;
  subDestination: string;
};

// Snapshot at attach time, so reporting survives the source experience changing or disappearing.
export type AttachedExperience = {
  id: string;
  dealOptionName: string;
  ancillarySellPriceUplift: number;
  valueCostCurrency: number;
  currency: string;
  costAud: number;
};

// The room-side counterpart of assetKey. Asset names are not unique, so never key on the name alone.
export const roomAssetKey = (room: Pick<CategoryRoom, 'assetName' | 'assetSfId'>) => room.assetSfId ?? room.assetName;

export const roomVariantKey = (room: Pick<CategoryRoom, 'assetName' | 'assetSfId' | 'packageName'>) =>
  `${roomAssetKey(room)}::${room.packageName}`;

export type CategoryRoom = {
  id: number;
  assetName: string;
  assetSfId?: string;
  asiaOnly: boolean;
  packageName: string;
  upliftType?: UpliftType;
  experiences?: Array<AttachedExperience>;
  propertyNetLift?: number;
};

export type RoomPricing = {
  allocation: string;
  occupancy: string;
  sellByLos: Record<number, string>;
  sellEditedByLos: Record<number, boolean>;
  netByLos: Record<number, string>;
  extraNightSell: string;
  extraNightSellEdited: boolean;
  extraNightNet: string;
  otaPrice: string;
};

export type NetRateFormulaState = {
  netPercent: string;
  applyToExtraNight: boolean;
  scope: 'all' | 'choose';
  selectedRoomIds: Array<number>;
};

export type OptionState = {
  lengthsOfStay: Array<number>;
  roomsByCategory: Array<Array<CategoryRoom>>;
  selectedLos: number;
  pricingByRoomId: Record<number, RoomPricing>;
  netRateFormula: NetRateFormulaState;
};
